import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { MainLayout } from "./layouts/MainLayout";
import { ProductList } from "./pages/ProductList";
import { ProductDetail } from "./pages/ProductDetail";
import { ProductDetailInfo } from "./pages/ProductDetailInfo";
import { Orders } from "./pages/Orders";
import { OrderForm } from "./pages/OrderForm";
import { CustomersList as Customers } from "./pages/CustomersV2/List";
import { CustomerDetail } from "./pages/customers/CustomerDetail";
import { CustomerCreate } from "./pages/CustomerCreate";
import { BeneficiaryEdit } from "./pages/customers/BeneficiaryEdit";
import { InsuredEdit } from "./pages/customers/InsuredEdit";
import { Profile } from "./pages/Profile";
import { OrderCorrectionList } from "./pages/OrderCorrectionList";
import { OrderAuditLog } from "./pages/OrderAuditLog";
import { RiskAssessmentForm } from "./pages/OrderForms/RiskAssessmentForm";
import { OrderSuccess } from "./pages/OrderForms/OrderSuccess";
import { OrderDetail } from "./pages/OrderDetail";
import { OrderSubmitSuccess } from "./pages/OrderSubmitSuccess";
import CustomerConfirm from "./pages/h5/CustomerConfirm";
import CustomerConfirmSuccess from "./pages/h5/CustomerConfirmSuccess";
import FilePreview from "./pages/FilePreview";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  useMetadataStore,
  ensureAuthenticated,
  useConfigStore,
  getUserInfo,
} from "@ifa/utils";
import { isIOSStandaloneMode } from "@/utils/platform";
import KeyboardTestPage from "./pages/KeyboardTestPage";
import "./App.css";
import { useViewportHeight } from "@/hooks/useViewportHeight";
import { DebugPanel } from "./components/DebugPanel";
import { Spin } from "antd";

function App() {
  const brandConfig = useConfigStore((state) => state.brandConfig);

  useEffect(() => {
    document.title = brandConfig.name;

    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (link) {
      link.href = brandConfig.favicon;
    }
  }, [brandConfig.name, brandConfig.favicon]);

  useEffect(() => {
    if (isIOSStandaloneMode()) {
      document.documentElement.classList.add("ios-standalone");
      document.body.classList.add("ios-standalone");
      console.log("=== iOS Standalone Mode 检测 ===");
      console.log("✅ Standalone Mode 已激活");
      console.log("window.innerHeight:", window.innerHeight);
      console.log("visualViewport.height:", window.visualViewport?.height);
      console.log(
        "差异:",
        window.innerHeight - (window.visualViewport?.height || 0),
        "px",
      );
    } else {
      console.log("=== 普通浏览器模式 ===");
    }
  }, []);

  useViewportHeight();

  const location = useLocation();
  const fetchMetadata = useMetadataStore((state) => state.fetchMetadata);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [metadataReady, setMetadataReady] = useState(false);
  const [tenantResolvedUid, setTenantResolvedUid] = useState<string | null>(
    null,
  );

  const isPublicRoute = location.pathname.startsWith("/h5");
  const currentUid = isAuthed ? localStorage.getItem("uid") : null;

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      if (isPublicRoute) {
        if (!cancelled) {
          setIsChecking(false);
        }
        return;
      }

      const authenticated = await ensureAuthenticated();

      if (cancelled) return;

      if (authenticated) {
        useConfigStore
          .getState()
          .syncRuntimeTenantWithCurrentDomain(
            localStorage.getItem("tenantCode"),
          );
        setIsAuthed(true);
      } else {
        setIsAuthed(false);
      }

      setIsChecking(false);
    };

    void checkAuth();

    return () => {
      cancelled = true;
    };
  }, [isPublicRoute]);

  useEffect(() => {
    if (!isAuthed) {
      setTenantResolvedUid(null);
      return;
    }
    if (!currentUid || currentUid === tenantResolvedUid) {
      return;
    }

    let cancelled = false;

    const syncUserTenant = async () => {
      try {
        const info = await getUserInfo({ uid: currentUid });
        if (cancelled) return;

        if (info.instance) {
          localStorage.setItem("tenantCode", info.instance);
          useConfigStore
            .getState()
            .syncRuntimeTenantWithCurrentDomain(info.instance);
        }
      } catch (error) {
        console.error("获取用户信息失败", error);
      } finally {
        if (!cancelled) {
          setTenantResolvedUid(currentUid);
        }
      }
    };

    void syncUserTenant();

    return () => {
      cancelled = true;
    };
  }, [currentUid, isAuthed, tenantResolvedUid]);

  useEffect(() => {
    if (!isAuthed) {
      setMetadataReady(false);
      return;
    }
    if (metadataReady) {
      return;
    }

    let cancelled = false;

    const loadMetadata = async () => {
      try {
        await fetchMetadata();
      } catch (error) {
        console.error("加载元数据失败:", error);
      } finally {
        if (!cancelled) {
          setMetadataReady(true);
        }
      }
    };

    void loadMetadata();

    return () => {
      cancelled = true;
    };
  }, [fetchMetadata, isAuthed, metadataReady]);

  if (isChecking) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!isPublicRoute && isAuthed && !metadataReady) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthed && !isPublicRoute) {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <DebugPanel />
      <Routes>
        <Route
          path="/h5/order/confirm/:orderId/:flowRecordId"
          element={<CustomerConfirm />}
        />
        <Route path="/h5/order/success" element={<CustomerConfirmSuccess />} />
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Navigate to="/orders" replace />} />
          <Route path="products" element={<ProductList />} />
          <Route path="products/:productId" element={<ProductDetail />} />
          <Route
            path="products/:productId/info"
            element={<ProductDetailInfo />}
          />
          <Route path="orders" element={<Orders />} />
          <Route path="customers" element={<Customers />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        {/* Full screen pages without sidebar */}
        <Route path="product-select" element={<ProductList />} />
        <Route path="orders/new" element={<OrderForm />} />
        <Route path="orders/confirmation" element={<OrderForm />} />
        <Route path="preview" element={<FilePreview />} />
        <Route
          path="orders/:orderId"
          element={
            <ErrorBoundary>
              <OrderDetail />
            </ErrorBoundary>
          }
        />
        <Route path="orders/:orderId/success" element={<OrderSuccess />} />
        <Route
          path="orders/:orderId/submit-success"
          element={<OrderSubmitSuccess />}
        />
        <Route path="orders/:orderId/risk" element={<RiskAssessmentForm />} />
        <Route path="orders/:orderId/edit" element={<OrderForm />} />
        <Route
          path="orders/:orderId/correction"
          element={<OrderCorrectionList />}
        />
        <Route path="orders/:orderId/audit-log" element={<OrderAuditLog />} />
        <Route
          path="orders/:orderId/correction/:recordId/edit"
          element={<OrderForm />}
        />

        <Route path="customers/new" element={<CustomerCreate />} />
        <Route path="customers/:customerId" element={<CustomerDetail />} />
        <Route
          path="customers/:customerId/beneficiaries/:beneficiaryId/edit"
          element={<BeneficiaryEdit />}
        />
        <Route
          path="customers/:customerId/insureds/:insuredId/edit"
          element={<InsuredEdit />}
        />

        <Route path="test/keyboard" element={<KeyboardTestPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
